import { AnimatePresence, motion } from 'framer-motion'
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '../components/Button'
import { FilterBar, FilterSelect, SearchInput } from '../components/FilterBar'
import { PageHeader } from '../components/PageHeader'
import { TaskCard } from '../components/TaskCard'
import { TaskDetailDrawer } from '../components/TaskDetailDrawer'
import { useAppData } from '../context/AppDataContext'
import { useToast } from '../context/ToastContext'
import { useJustArrived } from '../hooks/useJustArrived'
import { useScopedWork } from '../hooks/useScopedWork'
import { DONE_STATUS, type Task, type TaskStatus } from '../data/types'
import { cn } from '../lib/cn'
import { NewTaskModal } from './board/NewTaskModal'

/**
 * Seven columns, in the order work actually moves through them.
 *
 * The design shipped with five generic ones. These are the studio's, and the
 * two extra are the point: `Editing` and `With client` are where the estate
 * lost most of its work, and a board that folds them into "In Progress" is a
 * board that cannot tell you whether you are waiting on yourselves or on them.
 *
 * The colour ramp warms towards the client and cools once it is out of the
 * studio's hands — grey while it is only an idea, amber while somebody is
 * working, magenta at the moment it needs a decision, green once it is up.
 */
const columns: { status: TaskStatus; color: string }[] = [
  { status: 'Briefed', color: '#91919c' },
  { status: 'Shot', color: '#3b7dde' },
  { status: 'Editing', color: '#d98e1c' },
  { status: 'Review', color: '#FC2265' },
  { status: 'With client', color: '#7c4dff' },
  { status: 'Scheduled', color: '#0f9b9b' },
  { status: DONE_STATUS, color: '#2f9e57' },
]

export default function Board() {
  const { setTaskStatus, directory } = useAppData()
  const { tasks, clients, isScoped } = useScopedWork()
  const activeClients = clients.filter(c => c.accountStatus !== 'Former' && c.accountStatus !== 'Lost')
  const { showToast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  // Captured directly from the URL at mount time (not in an effect): calling
  // setSearchParams below to tidy the URL goes through React Router's own
  // navigate machinery, which remounts this route element — an effect-driven
  // capture would have its state wiped by that remount before the user ever
  // sees the pulse. Reading it into the initial state here means it survives
  // regardless of what a later remount does.
  const [pulseTaskId, setPulseTaskId] = useState<string | null>(() => searchParams.get('highlight'))
  const [search, setSearch] = useState('')
  const [clientFilter, setClientFilter] = useState('all')
  const [assigneeFilter, setAssigneeFilter] = useState('all')
  const [priorityFilter, setPriorityFilter] = useState('all')
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)
  const [newTaskStatus, setNewTaskStatus] = useState<TaskStatus | null>(null)
  const [dragOverCol, setDragOverCol] = useState<TaskStatus | null>(null)
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const columnsRef = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  // Trackpad users can two-finger-swipe the columns horizontally for free,
  // but mouse users only get the scrollbar at the very bottom of a tall
  // page — these buttons step through the columns instead.
  //
  // One column per press on mobile, where a single board fills the screen
  // and jumping two would skip one unseen; two on wider screens, where
  // several are visible at once. The step is measured off the rendered
  // column rather than hardcoded, so it stays correct as the responsive
  // width changes.
  function scrollColumns(direction: 1 | -1) {
    const el = columnsRef.current
    if (!el) return
    const firstColumn = el.firstElementChild as HTMLElement | null
    const GAP = 16
    const step = (firstColumn?.offsetWidth ?? 300) + GAP
    const columnsPerStep = window.matchMedia('(min-width: 640px)').matches ? 2 : 1
    el.scrollBy({ left: direction * columnsPerStep * step, behavior: 'smooth' })
  }

  // Arriving from a notification like "?highlight=tk-1": clear any
  // filters that might be hiding the task, once, on mount only.
  useEffect(() => {
    if (!pulseTaskId) return
    setSearch('')
    setClientFilter('all')
    setAssigneeFilter('all')
    setPriorityFilter('all')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filtered = useMemo(() => {
    return tasks.filter((t) => {
      if (search && !t.title.toLowerCase().includes(search.toLowerCase())) return false
      if (clientFilter !== 'all' && t.clientId !== clientFilter) return false
      if (assigneeFilter !== 'all' && t.assigneeId !== assigneeFilter) return false
      if (priorityFilter !== 'all' && t.priority !== priorityFilter) return false
      return true
    })
  }, [tasks, search, clientFilter, assigneeFilter, priorityFilter])

  const arrived = useJustArrived(tasks)
  useEffect(() => {
    if (!pulseTaskId) return
    cardRefs.current.get(pulseTaskId)?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
  }, [pulseTaskId, filtered])

  // Only tidy the URL once the pulse has actually played out — stripping the
  // param any earlier, via React Router's setSearchParams, remounts this
  // route element and would cut the animation off before it's seen.
  function clearPulse() {
    setPulseTaskId(null)
    if (searchParams.has('highlight')) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete('highlight')
          return next
        },
        { replace: true },
      )
    }
  }

  const openTask = tasks.find((t) => t.id === selectedTask?.id) ?? null

  function handleDrop(status: TaskStatus) {
    if (draggingId) {
      // A drag is the easiest thing on this page to do by accident — a card
      // picked up while scrolling lands in the next column and the clock on
      // the stage it left has already stopped. Moving it back is the same
      // call, so the toast can offer it.
      const id = draggingId
      const was = tasks.find((t) => t.id === id)?.status
      setTaskStatus(id, status)
      showToast(
        `Moved to ${status.toLowerCase()}`,
        'success',
        was === undefined || was === status
          ? undefined
          : { undo: () => setTaskStatus(id, was), undoLabel: `Back to ${was.toLowerCase()}` }
      )
    }
    setDraggingId(null)
    setDragOverCol(null)
  }

  return (
    <div className="flex h-full flex-col gap-5">
      <PageHeader
        variant="dark"
        title="Board"
        description="Every client’s work at once, and how long each piece has sat there."
        actions={
          <Button onClick={() => setNewTaskStatus('Briefed')}>
            <Plus size={16} /> Add a piece
          </Button>
        }
      />

      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search pieces…" className="w-full sm:w-64" />
        <FilterSelect
          label="Filter by client"
          value={clientFilter}
          onChange={setClientFilter}
          options={[{ label: 'All clients', value: 'all' }, ...activeClients.map((c) => ({ label: c.name, value: c.id }))]}
        />
        {!isScoped && (
          <FilterSelect
            label="Filter by owner"
            value={assigneeFilter}
            onChange={setAssigneeFilter}
            options={[{ label: 'Anyone', value: 'all' }, ...directory.map((m) => ({ label: m.name, value: m.id }))]}
          />
        )}
        <FilterSelect
          label="Filter by priority"
          value={priorityFilter}
          onChange={setPriorityFilter}
          options={[
            { label: 'All priorities', value: 'all' },
            { label: 'Low', value: 'Low' },
            { label: 'Medium', value: 'Medium' },
            { label: 'High', value: 'High' },
            { label: 'Urgent', value: 'Urgent' },
          ]}
        />
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => scrollColumns(-1)}
            aria-label="Scroll columns left"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-500 text-white shadow-pop transition-transform hover:scale-105 hover:bg-brand-600 active:scale-95"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => scrollColumns(1)}
            aria-label="Scroll columns right"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-500 text-white shadow-pop transition-transform hover:scale-105 hover:bg-brand-600 active:scale-95"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </FilterBar>

      {/* snap-mandatory on mobile only: a swipe settles on exactly one
        * board rather than drifting between two. Wider screens show several
        * columns at once, where snapping would fight free scrolling. */}
      <div
        ref={columnsRef}
        className="flex flex-1 items-start gap-4 overflow-x-auto pb-4 scrollbar-thin -mx-1 px-1 snap-x snap-mandatory sm:snap-none"
      >
        {columns.map((col) => {
          const colTasks = filtered.filter((t) => t.status === col.status)
          return (
            <div
              key={col.status}
              onDragOver={(e) => {
                e.preventDefault()
                setDragOverCol(col.status)
              }}
              onDragLeave={() => setDragOverCol((prev) => (prev === col.status ? null : prev))}
              onDrop={() => handleDrop(col.status)}
              className={cn(
                // Full width on mobile so exactly one board is on screen at
                // a time; fixed 300px once there's room for several.
                'flex w-full shrink-0 snap-start flex-col rounded-2xl border bg-shell p-3 transition-colors sm:w-[300px]',
                dragOverCol === col.status ? 'border-brand-300 bg-brand-500/10' : 'border-shell-border',
              )}
            >
              <div className="mb-3 flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: col.color }} />
                  <h3 className="text-sm font-semibold text-shell-text-active">{col.status}</h3>
                  <span className="rounded-full bg-shell-border/50 px-1.5 py-0.5 text-xs font-medium text-shell-text border border-shell-border">
                    {colTasks.length}
                  </span>
                </div>
                <button
                  onClick={() => setNewTaskStatus(col.status)}
                  aria-label={`Add a piece to ${col.status}`}
                  className="rounded-md p-1 text-shell-text hover:bg-shell-border/50 hover:text-brand-500"
                >
                  <Plus size={15} />
                </button>
              </div>

              <div className="flex flex-1 flex-col gap-2.5 min-h-[80px]">
                <AnimatePresence>
                  {colTasks.map((task) => (
                    <div
                      key={task.id}
                      ref={(el) => {
                        if (el) cardRefs.current.set(task.id, el)
                        else cardRefs.current.delete(task.id)
                      }}
                      draggable
                      onDragStart={() => setDraggingId(task.id)}
                      onDragEnd={() => setDraggingId(null)}
                      className={cn(
                        'rounded-2xl ring-offset-2 ring-offset-ink-50 transition-shadow duration-500',
                        draggingId === task.id && 'opacity-40',
                        arrived.has(task.id) && 'ring-2 ring-brand-400',
                      )}
                    >
                      <TaskCard
                        task={task}
                        onOpen={() => setSelectedTask(task)}
                        pulse={pulseTaskId === task.id}
                        onPulseEnd={clearPulse}
                      />
                    </div>
                  ))}
                </AnimatePresence>
                {colTasks.length === 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-shell-border py-8 text-xs text-shell-text"
                  >
                    Nothing here
                  </motion.div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <TaskDetailDrawer task={openTask} onClose={() => setSelectedTask(null)} />
      <NewTaskModal
        open={newTaskStatus !== null}
        onClose={() => setNewTaskStatus(null)}
        defaultStatus={newTaskStatus ?? 'Briefed'}
      />
    </div>
  )
}
